import { Stack } from "@mui/material";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  createErrorComponent,
  createErrorSnackBar,
} from "components/Errors/ErrorComponent";
import {
  NotificationRead,
  NotificationType,
} from "constants/NotificationConstants";
import { UNAUTHORIZED_ERROR_CODE } from "constants/error/ErrorCodes";
import { NOTIFICATIONS_ZONE, PARAM_KEY } from "constants/navigation";
import { useAppDispatch, useAppSelector } from "hooks/StoreHooks";
import { useGetNotificationList } from "hooks/useGetNotificationList";
import { useImportantNotificationPopup } from "hooks/useImportantNotificationPopup";
import { NotificationResource } from "models/Resources/NotificationResource";
import { transformNotificationResponseToResource } from "models/Responses/NotificationResponse";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { usePostNotificationReadMutation } from "services/api/warehouseApi";
import { selectStoreNb } from "store/Selectors/locationsSelector";
import { selectNotificationsList } from "store/Selectors/notificationSelector";
import { logout } from "store/Slices/appStateSlice";
import {
  getNewImportantNotification,
  updateNotification,
} from "store/Slices/gridEventSlice";
import { addNewURLQueryParams } from "utils/getNewURLQueryParams";
import { getUrlParamOrLocalStorage } from "utils/getUrlParamOrLocalStorage";
import { getUsername } from "utils/getUsername";
import NotificationZoneHeader from "./NotificationZoneHeader/NotificationZoneHeader";
import NotificationZoneHeaderButtons from "./NotificationZoneHeader/NotificationZoneHeaderButtons";
import NotificationDetails from "./Notifications/NotificationDetails/NotificationDetails";
import NotificationList from "./Notifications/NotificationList/NotificationList";
import NotificationSnackBar from "./Snackbars/NotificationSnackBar";

import "../../pages/Home/Home.scss";
import "./NotificationZone.scss";

export const ALL_SECTION_NAME = "Toutes";
export const IMPORTANT_SECTION_NAME = "Importantes";
export const SNACKBAR_DURATION_MS = 10 * 6000;

const NotificationZone: React.FC = () => {
  const storeNb = useAppSelector(selectStoreNb);
  const token = useRef(useAppSelector(state => state.appState.token));
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedNotificationSectionType, setSelectedNotificationType] =
    useState(ALL_SECTION_NAME);

  const [isExpanded, setExpanded] = useState(false);
  const [expandedNotificationId, setExpandedNotificationId] =
    useState<string>("");
  const [isAnchored, setAnchored] = useState<boolean>(false);

  const notifications = useAppSelector(selectNotificationsList);
  const { isLoading: isGetNotificationsLoading, error: notificationListError } =
    useGetNotificationList(storeNb);
  const newImportantNotification = useAppSelector(getNewImportantNotification);
  const importantNotifications: NotificationResource[] = useMemo(
    () =>
      notifications
        ? notifications?.filter(
            notification => notification.type == NotificationType.IMPORTANT
          )
        : [],
    [notifications]
  );
  useImportantNotificationPopup(notifications, {
    autoHideMs: SNACKBAR_DURATION_MS,
  });

  useEffect(() => {
    const defaultAnchoredValue = getUrlParamOrLocalStorage(
      PARAM_KEY.NOTIFICATIONS,
      searchParams
    );
    if (defaultAnchoredValue) {
      localStorage.setItem(PARAM_KEY.NOTIFICATIONS, defaultAnchoredValue);
      if (defaultAnchoredValue === NOTIFICATIONS_ZONE.ANCHORED) {
        setAnchored(true);
        const newURLParams = addNewURLQueryParams(
          NOTIFICATIONS_ZONE.ANCHORED,
          PARAM_KEY.NOTIFICATIONS,
          searchParams
        );
        setSearchParams(newURLParams);
      }
    }
  }, [searchParams, isExpanded, setSearchParams, dispatch]);

  useEffect(() => {
    if (isAnchored) setExpanded(true);
  }, [isAnchored]);

  const [
    sendNotificationRead,
    {
      isSuccess: isPostSuccess,
      data: postResponse,
      isLoading: isPostLoading,
      error,
    },
  ] = usePostNotificationReadMutation();

  useEffect(() => {
    if (!isGetNotificationsLoading && !isExpanded) {
      setExpanded(getNonReadNotifications(importantNotifications).length > 0);
    }
  }, [isGetNotificationsLoading, importantNotifications]);

  useEffect(() => {
    if (notificationListError) {
      notificationListError as FetchBaseQueryError;
      if (notificationListError.status == UNAUTHORIZED_ERROR_CODE) {
        dispatch(logout());
      }
    }
  });

  useEffect(() => {
    if (isPostSuccess && postResponse) {
      const postResource =
        transformNotificationResponseToResource(postResponse);
      dispatch(updateNotification(postResource));
    }
  }, [isPostSuccess, isPostLoading]);

  function handleSectionChange(newSectionType: string) {
    setSelectedNotificationType(newSectionType);
  }

  function handleSectionClick(): void {
    setExpanded(true);
  }

  function handleExpandToggle() {
    if (!isAnchored) {
      setExpanded(!isExpanded);
    }
  }

  function handleNotificationClick(
    notificationType: NotificationType,
    notificationId: string
  ) {
    if (notificationType == NotificationType.NORMAL) {
      handleNotificationReadClick(notificationId);
    }
    setExpandedNotificationId(notificationId);
  }

  const handleNotificationReadClick = async (notificationId: string) => {
    setExpandedNotificationId("");
    markAsRead(notificationId);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const user = getUsername();
      const date = new Date().toISOString();
      const notificationReadJSON = makeNotificationReadJSON(date, user);

      await sendNotificationRead({
        storeNb: storeNb,
        notificationId: notificationId,
        action: JSON.stringify(notificationReadJSON),
        token: token.current,
      });
    } catch (err) {
      console.error("Erreur lors de la lecture des notifications", err);
    }
  };

  function getNotificationById(id: string): NotificationResource {
    const notification = notifications!.find(
      notification => notification.notificationId == id
    );
    return notification!;
  }

  function returnToNotificationList(): void {
    setExpandedNotificationId("");
  }

  function handleReturnLogoClick(): void {
    returnToNotificationList();
  }

  function handleToggleAnchor() {
    if (!isAnchored) {
      setExpanded(true);
    }
    const newURLParams = addNewURLQueryParams(
      isAnchored ? NOTIFICATIONS_ZONE.UNANCHORED : NOTIFICATIONS_ZONE.ANCHORED,
      PARAM_KEY.NOTIFICATIONS,
      searchParams
    );
    setSearchParams(newURLParams);
    setAnchored(!isAnchored);
  }

  if (error || notificationListError) {
    if (error && "status" in error) {
      return createErrorComponent(error);
    } else if (notificationListError && "status" in notificationListError) {
      createErrorSnackBar(
        notificationListError.status +
          "Erreur lors de la récupération des notifications"
      );
    }
    return createErrorSnackBar("Erreur inconnue des notifications");
  }

  return (
    <>
      <Stack
        className={`notification-groups ${isAnchored ? "anchored" : "float"}`}
        direction="column"
        sx={{
          alignItems: "flex-end",
        }}>
        <Stack id="notification-zone" className={isAnchored ? "anchored" : "float"}>
          <Stack id="notification-zone-stack" className={isAnchored ? "anchored" : "float"}>
            <NotificationZoneHeader
              isAnchored={isAnchored}
              isNotificationExpanded={isAnchored ? true : isExpanded}
              handleExpandToggle={handleExpandToggle}
              handleToggleAnchor={handleToggleAnchor}
            />
            <NotificationZoneHeaderButtons
              selectedNotificationSectionType={selectedNotificationSectionType}
              onSectionChange={handleSectionChange}
              onSectionClick={handleSectionClick}
              nbUnreadImportantNotifications={
                getNonReadNotifications(importantNotifications).length
              }
              nbUnreadNotifications={
                getNonReadNotifications(notifications).length
              }
            />
            {isExpanded ? (
              !expandedNotificationId ? (
                <NotificationList
                  notifications={
                    selectedNotificationSectionType == ALL_SECTION_NAME
                      ? notifications
                      : importantNotifications
                  }
                  isAnchored={isAnchored}
                  handleNotificationClick={handleNotificationClick}
                />
              ) : (
                <NotificationDetails
                  notification={getNotificationById(expandedNotificationId)}
                  onNotificationReadClick={handleNotificationReadClick}
                  onReturnLogoClick={handleReturnLogoClick}
                />
              )
            ) : null}
          </Stack>
        </Stack>
      </Stack>
      <NotificationSnackBar
        newImportantNotification={newImportantNotification}
        handleNotificationReadClick={handleNotificationReadClick}
      />
    </>
  );
};

export default NotificationZone;

export function getNonReadNotifications(
  notifications: NotificationResource[]
): NotificationResource[] {
  return notifications.filter(notification => notification.archivedBy == null);
}

export function getReadNotifications(
  notifications: NotificationResource[]
): NotificationResource[] {
  return notifications.filter(notification => notification.archivedBy != null);
}

function makeNotificationReadJSON(
  date: string,
  user: string | null
): NotificationRead {
  return { luLe: date, luPar: user };
}
